import {
  contrastColor,
  luminosity,
  darken,
  lighten,
} from '@mentimeter/ragnar-colors';
import { range } from 'd3-array';
import { d3, wordWrap, shouldUpdateText } from './d3';
import { LARGE_FONT_SIZE } from './utils';

const DEFAULT_TRANSITION_TIME = 1000;
const LAYOUT = {
  width: 960,
  height: 540,
};
const RADAR_SIZE = LAYOUT.height;
const MARGIN = RADAR_SIZE * 0.15;
const CHART_WIDTH = RADAR_SIZE - 2 * MARGIN;
const TEXT_BOX_WIDTH = CHART_WIDTH / 2;

interface Axis {
  axis: string;
  value: number;
  orgValue: number;
  x?: number;
  y?: number;
}

interface Range {
  min: number;
  max: number;
}

interface SpiderResult {
  id: string;
  score: { [id: string]: number };
  skips: number;
  label: string;
}

interface SpiderProps {
  selector: string;
  results: SpiderResult[];
  range: Range;
  animationMultiplier: 0 | 1;
  fillColors: string[];
  textColor: string;
  lineColor: string;
}

interface Data {
  axes: Axis[];
}

const axisKeyAccessor = (_: Data, i: number) => i;

/**
 * Spider: webbed result layout for scales data
 */
export class Spider implements SpiderProps {
  readonly selector: string;
  readonly results: SpiderResult[];
  readonly range: Range;
  readonly animationMultiplier: 0 | 1;
  readonly fillColors: string[];
  readonly textColor: string;
  readonly lineColor: string;

  /**
   * @param selector: string selection to a dom node, e.g., #spider-${questionId}
   * @param results: result data on a scale of x to y
   * @param range: range of possible values, e.g., 1-5
   * @param animationMultiplier: value to multiply animation durations by, 1 (enabled) or 0 (disabled)
   * @param fillColors: array of colors for bars, e.g., ['green', 'yellow', 'blue']
   * @param textColor: color string, e.g., #e8e8e8
   * @param lineColor: color string, e.g., #e8e8e8
   */
  constructor(props: SpiderProps) {
    this.selector = props.selector;
    this.results = props.results;
    this.range = props.range;
    this.animationMultiplier = props.animationMultiplier;
    this.fillColors = props.fillColors;
    this.textColor = props.textColor;
    this.lineColor = props.lineColor;
  }

  /**
   * Darken or brighten a color depending on its original brightness
   * (e.g., white becomes darker, black becomes brighter)
   *
   * @param color: color string to alter
   * @param amount: value from 0-1, how much the color should be adjusted by
   * @returns color string that is lighter or darker than the original
   */
  private darkenOrBrighten = (color: string, amount: number): string => {
    if (luminosity(color) > 0.5) return darken(color, amount);
    return lighten(color, amount);
  };

  /**
   * Re-orders array items to optimize for "clockwise" rendering in the UI
   *
   * @param array: list of items to re-order
   * @returns same array with changed order
   */
  private clockwise = (array: Axis[]): Axis[] => {
    const shiftedArr = array.concat(array.splice(0, 1));
    return shiftedArr.reverse();
  };

  /**
   * Compute the mean value based on votes
   *
   * @param index: result index whose score should be computed
   * @param normalized: normalize score on positive scale of 0-1
   * @returns number score
   * @description Get score is only exposed in order to test it easily. Do not use it in production.
   */
  getScore = ({
    index,
    normalized,
  }: {
    index: number;
    normalized: boolean;
  }): number => {
    const initialScores: Record<number, number> = {};
    for (let i = 0; i < this.range.max; i++) {
      initialScores[i + 1] = 0;
    }
    const scores = this.results[index]?.score || initialScores;
    let tot = 0;
    for (let i = 0; i < Object.keys(scores).length; i++) {
      const key = parseInt(Object.keys(scores)[i] ?? '', 10);
      // shift so that min value is 0;
      // and change range to [0,1]
      const keyNormalized =
        (key - this.range.min) / Math.abs(this.range.max - this.range.min);
      const score = (normalized ? keyNormalized : key) * (scores[key] ?? 0);
      tot += score;
    }
    const totalResponses = Object.values(scores).reduce(
      (sum, count) => sum + count,
      0,
    );
    const mean = totalResponses > 0 ? tot / totalResponses : 0;
    return isNaN(mean) ? 0 : mean;
  };

  /**
   * Get the list of scores and labels based on incoming results
   *
   * @returns list of values on each axis
   */
  private getAxisData = (): Axis[] => {
    return this.results.map((r, index) => ({
      axis: r.label,
      value: this.getScore({
        index,
        normalized: true,
      }),
      orgValue: this.getScore({
        index,
        normalized: false,
      }),
    }));
  };

  /**
   * Creates all visible features in the visualization and adds color, shape,
   * animations, etc. based on bound data
   *
   * @returns array of promises (animations to be resolved)
   */
  private updateChart = (): Promise<void[]> => {
    const { results, textColor, fillColors, lineColor, darkenOrBrighten } =
      this;
    const transitions: Promise<void>[] = [];
    const factor = 0.95;
    const factorLegend = 1;
    let maxValue = 1;
    const minValue = 0;
    const radians = 2 * Math.PI;
    const radius = 15;
    const circleValueFormat = (value: number): string => value.toFixed(1);
    const w = CHART_WIDTH;
    const h = CHART_WIDTH;
    const transitionDuration =
      DEFAULT_TRANSITION_TIME * this.animationMultiplier;
    const levels = Math.min(5, Math.abs(this.range.max - this.range.min));

    d3.select<SVGGElement, Data[]>(this.selector).each(function (data) {
      const container = d3.select(this);
      const [axes] = data.map((d) => d.axes);
      const labels = axes?.map((d) => d.axis) || [];
      const values = axes?.map((d) => d.value) || [];
      maxValue = Math.max(maxValue, Math.max(...values));
      maxValue -= minValue;
      const total = labels.length;
      const radius1 = factor * Math.min(w / 2, h / 2);
      const radius2 = Math.min(w / 2, h / 2);

      function getPosition({
        i,
        range,
        func,
        factor,
      }: {
        i: number;
        range: number;
        func: (x: number) => number;
        factor?: number | undefined;
      }) {
        const f = typeof factor !== 'undefined' ? factor : 1;
        return range * (1 - f * func((i * radians) / total));
      }
      function getHorizontalPosition(
        i: number,
        range: number,
        factor?: number,
      ) {
        return getPosition({ i, range, func: Math.sin, factor });
      }
      function getVerticalPosition(i: number, range: number, factor?: number) {
        return getPosition({ i, range, func: Math.cos, factor });
      }

      const levelFactors = range(0, levels).map(
        (level) => radius1 * ((level + 1) / levels),
      );

      // Select
      const levelGroups = container
        .selectAll<SVGGElement, number>('g.level-group')
        .data(levelFactors);
      // Enter
      const levelGroupsEnter = levelGroups
        .enter()
        .append('g')
        .attr('class', (_d, i) => `level-group level-group-${i}`);
      // Exit
      levelGroups.exit().remove();

      // Select
      const levelLine = levelGroups
        .merge(levelGroupsEnter)
        .selectAll<SVGLineElement, number>('.level')
        .data((levelFactor) => range(0, total).map(() => levelFactor));
      // Enter
      const levelEnter = levelLine
        .enter()
        .append('line')
        .attr('class', 'level')
        .attr('stroke-width', 2);
      // Update
      levelLine
        .merge(levelEnter)
        .attr('stroke', () => darkenOrBrighten(lineColor, 0.5))
        .attr('x1', (levelFactor, i) => getHorizontalPosition(i, levelFactor))
        .attr('y1', (levelFactor, i) => getVerticalPosition(i, levelFactor))
        .attr('x2', (levelFactor, i) =>
          getHorizontalPosition(i + 1, levelFactor),
        )
        .attr('y2', (levelFactor, i) => getVerticalPosition(i + 1, levelFactor))
        .attr(
          'transform',
          (levelFactor) =>
            `translate(${w / 2 - levelFactor}, ${h / 2 - levelFactor})`,
        );
      // Exit
      levelLine.exit().remove();

      // Select
      const axis = container
        .selectAll<SVGGElement, string>('.axis')
        .data(labels);
      // Enter
      const axisEnter = axis
        .enter()
        .insert('g', ':first-child')
        .attr('class', (_, i) => `axis axis-${i}`);
      const axisText = axisEnter.append('text');
      // Update
      const selection = axis.select<SVGTextElement>('text').merge(axisText);
      selection
        .attr(
          'x',
          (_d, i) =>
            w / 2 - radius2 + getHorizontalPosition(i, radius2, factorLegend),
        )
        .attr(
          'y',
          (_d, i) =>
            h / 2 - radius2 + getVerticalPosition(i, radius2, factorLegend),
        )
        .attr('fill', textColor)
        .attr('text-anchor', (_, i) => {
          const h = getHorizontalPosition(i, 0.5);
          const left = h < 0.4;
          const right = h > 0.6;
          if (left) return 'end';
          if (right) return 'start';
          return 'middle';
        })
        .attr('transform', function (_, i) {
          const h = getHorizontalPosition(i, 0.5);
          const v = parseFloat(getVerticalPosition(i, 0.5).toFixed(1));
          let dx = 0;
          let dy = 0;
          const spacing = 20;
          const { height } = (this as SVGTextElement).getBBox();
          const top = v < 0.5;
          const bottom = v > 0.5;
          const left = h < 0.4;
          const right = h > 0.6;
          const middle = !left && !right;
          const numOfLines = (this as SVGTextElement).childNodes.length || 1;

          if (middle) {
            if (top) {
              if (numOfLines > 1) {
                dy = -(height / numOfLines) * (numOfLines - 1);
              }
            } else if (bottom) {
              dy = height / 2 / numOfLines;
            }
          }
          if (top) {
            dy -= spacing;
          } else if (bottom) {
            dy += spacing;
          }
          if (left) {
            dx -= spacing;
          } else if (right) {
            dx += spacing;
          }

          return `translate(${dx}, ${dy})`;
        });
      if (
        shouldUpdateText({
          selection,
          texts: results.map((r) => r.label),
          maxWidth: TEXT_BOX_WIDTH,
          maxHeight: MARGIN,
        })
      ) {
        selection
          .text((d) => d)
          .call(wordWrap, {
            maxWidth: TEXT_BOX_WIDTH,
            maxHeight: MARGIN,
            fontSize: LARGE_FONT_SIZE,
          });
      }
      const axisLine = axisEnter.append('line');
      axis
        .select<SVGLineElement>('line')
        .merge(axisLine)
        .attr('stroke', () => darkenOrBrighten(lineColor, 0.1))
        .attr('stroke-width', 2)
        .attr('x1', w / 2)
        .attr('y1', h / 2)
        .attr(
          'x2',
          (_d, i) =>
            w / 2 - radius2 + getHorizontalPosition(i, radius2, factor),
        )
        .attr(
          'y2',
          (_d, i) => h / 2 - radius2 + getVerticalPosition(i, radius2, factor),
        );
      // Exit
      axis.exit().remove();

      data.forEach((d) =>
        d.axes.forEach((axis, i) => {
          const x =
            w / 2 -
            radius2 +
            getHorizontalPosition(
              i,
              radius2,
              (Math.max(axis.value - minValue, 0) / maxValue) * factor,
            );
          const y =
            h / 2 -
            radius2 +
            getVerticalPosition(
              i,
              radius2,
              (Math.max(axis.value - minValue, 0) / maxValue) * factor,
            );

          axis.x = isNaN(x) ? 0 : x;
          axis.y = isNaN(y) ? 0 : y;
        }),
      );
      // Select
      const polygon = container
        .selectAll<SVGPolygonElement, Data>('.area')
        .data(data, axisKeyAccessor);
      // Enter
      const polygonEnter = polygon
        .enter()
        .append('polygon')
        .attr('class', 'area')
        .attr('fill-opacity', 0.5)
        .attr('stroke-width', 2);
      // Update
      const polygonTransition = polygon
        .merge(polygonEnter)
        .attr('stroke', fillColors[0] ?? 'black')
        .attr('fill', fillColors[0] ?? 'black')
        .transition()
        .ease(d3.easeElastic)
        .duration(transitionDuration)
        .attr('points', (d) =>
          d.axes.map((p) => [p.x, p.y].join(',')).join(' '),
        )
        .end();
      // Exit
      polygon.exit().transition().duration(transitionDuration).remove();

      // Select
      const circleGroups = container
        .selectAll<SVGGElement, Data>('g.circle-group')
        .data(data, axisKeyAccessor);
      // Enter
      const circleGroupsEnter = circleGroups
        .enter()
        .append('g')
        .attr('class', 'circle-group');
      // Update
      const circleGroupsTransition = circleGroups
        .merge(circleGroupsEnter)
        .transition()
        .duration(transitionDuration)
        .end();
      // Exit
      circleGroups.exit().transition().duration(transitionDuration).remove();

      // Select
      const circle = circleGroups
        .merge(circleGroupsEnter)
        .selectAll('.circle')
        .data((d, i): [Axis, number][] => d.axes.map((d) => [d, i]));
      // Enter
      const circleEnter = circle.enter().append('g').attr('class', 'circle');
      const circleCircle = circleEnter
        .append('circle')
        .attr('fill', fillColors[0] ?? 'black')
        .attr('fill-opacity', 0.9);
      const circleText = circleEnter
        .append('text')
        .text((d) => circleValueFormat(d[0].orgValue))
        .attr('fill', () => contrastColor(fillColors[0] ?? 'black'))
        .attr('class', 'circle-value')
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle');
      // Update
      const circleTransition = circle
        .select<SVGCircleElement>('circle')
        .merge(circleCircle)
        .transition()
        .ease(d3.easeElastic)
        .duration(transitionDuration)
        .attr('r', radius)
        .attr('cx', (d) => d[0].x || 0)
        .attr('cy', (d) => d[0].y || 0)
        .attr('fill', fillColors[0] ?? 'black')
        .end();
      const textTransition = circle
        .select<SVGTextElement>('text')
        .merge(circleText)
        .text((d) => circleValueFormat(d[0].orgValue))
        .transition()
        .ease(d3.easeElastic)
        .duration(transitionDuration)
        .attr('fill', () => contrastColor(fillColors[0] ?? 'black'))
        .attr('x', (d) => d[0].x || 0)
        .attr('y', (d) => d[0].y || 0)
        .end();
      // Exit
      circle.exit().transition().duration(transitionDuration).remove();

      transitions.push(circleTransition);
      transitions.push(textTransition);
      transitions.push(circleGroupsTransition);

      // Add transitions to promise array
      transitions.push(polygonTransition);
    });

    return Promise.all(transitions);
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns array of methods that return promises (animations)
   */
  public update = (): Promise<void[][]> => {
    const axisData = this.getAxisData();
    const radarData = [{ axes: this.clockwise(axisData) }];
    d3.select(this.selector)
      .attr('class', 'focus')
      .datum(radarData)
      .attr(
        'transform',
        `translate(${LAYOUT.width / 2 - CHART_WIDTH / 2},${MARGIN})`,
      );
    const transitions = this.updateChart();

    return Promise.all([transitions]);
  };
}
